import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { authApi } from '@/services/api';
import { LoadingSpinner } from '@/components/common';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('Introduce tu email');
      return;
    }

    setLoading(true);

    try {
      // Backend always returns 200 with the same generic message — we don't
      // branch on the response. Even if the email is malformed or unknown,
      // we show the same confirmation to avoid leaking account existence.
      await authApi.forgotPassword(email);
      setSubmitted(true);
    } catch {
      // Network or server error — still show the success message so we
      // don't accidentally reveal anything. Toast a generic warning.
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-vida-900 to-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-vida-500 rounded-2xl mb-4">
            <span className="text-white font-bold text-3xl">V</span>
          </div>
          <h1 className="text-2xl font-bold text-white">VIDA System</h1>
          <p className="text-gray-400 mt-2">Recuperar acceso</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {!submitted ? (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                ¿Has olvidado tu contraseña?
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Introduce tu email y te enviaremos un enlace para restablecerla.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="input"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full py-3"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      Enviando...
                    </span>
                  ) : (
                    'Enviar enlace de recuperación'
                  )}
                </button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Volver a iniciar sesión
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                <span className="text-2xl">✉</span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Revisa tu correo
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Si su usuario está en nuestro sistema, le llegará un correo al correo
                indicado con instrucciones para restablecer su contraseña.
              </p>
              <p className="text-xs text-gray-400">
                Si no recibes el correo en unos minutos, revisa tu carpeta de spam.
                El enlace caduca a los 15 minutos.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-vida-600 hover:text-vida-700 font-medium"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Volver a iniciar sesión
              </Link>
            </div>
          )}
        </div>

        <p className="mt-8 text-center text-sm text-gray-500">
          VIDA System &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
